import { Link } from "@/lib/router-compat";
import {
  ArrowRight,
  Flag,
  Factory,
  Layers,
  ShieldCheck,
  Award,
  Users,
  TrendingUp,
  Sparkles,
} from "lucide-react";

interface MilestoneSummary {
  year: string;
  title: string;
}

const HERITAGE_MILESTONES: MilestoneSummary[] = [
  { year: "2002", title: "The Beginning" },
  { year: "2005", title: "Building Our Expertise" },
  { year: "2015", title: "Furniture & Joinery Manufacturing" },
  { year: "2020", title: "Expanding the Interior Ecosystem" },
  { year: "2022", title: "20 Years of Experience" },
  { year: "2024", title: "Investing in People" },
  { year: "2025", title: "Growing With International Standards" },
  { year: "2026", title: "Building the Future" },
];

const ICONS = [
  Flag,
  Factory,
  Layers,
  ShieldCheck,
  Award,
  Users,
  TrendingUp,
  Sparkles,
];

export default function HeritageSection() {
  const counters = [
    { value: "24+", label: "Years of Experience" },
    { value: "180+", label: "Direct Employees" },
    { value: "1500+", label: "Subcontractors" },
    { value: "3", label: "ISO Certifications" },
  ];

  return (
    <section
      className="relative bg-[#002b5c] text-white pt-4 pb-20 lg:pt-6 lg:pb-28 overflow-hidden"
      id="about"
    >
      {/* Background Subtle Gradient & Glow */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#001f48] via-[#002b5c] to-[#003875] opacity-90" />

      <div className="max-w-[1400px] mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left Column: History Timeline */}
          <div className="lg:col-span-6 relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-white/5 backdrop-blur-md p-6 sm:p-8">
              <span className="inline-block text-[#ffcc29] font-heading font-bold text-sm md:text-base tracking-[0.2em] uppercase mb-5">
                Our History
              </span>
              {/* Vertical timeline */}
              <div className="relative border-l-2 border-[#ffcc29]/40 ml-2 pl-6 space-y-5">
                {HERITAGE_MILESTONES.map((m, idx) => {
                  const Icon = ICONS[idx] || Sparkles;
                  return (
                    <div key={idx} className="relative group">
                      {/* Dot */}
                      <div className="absolute -left-[31px] top-1.5 w-4 h-4 rounded-full bg-[#ffcc29] border-4 border-[#001f48] shadow-[0_0_15px_rgba(255,204,41,0.5)]" />
                      <div className="flex items-center gap-3 mb-1">
                        <div className="w-9 h-9 rounded-full bg-[#ffcc29]/15 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                          <Icon className="w-4.5 h-4.5 text-[#ffcc29]" />
                        </div>
                        <div className="inline-block bg-[#ffcc29] text-[#002b5c] text-base font-extrabold font-heading px-2.5 py-0.5 rounded-sm">
                          {m.year}
                        </div>
                      </div>
                      <h3 className="text-lg font-heading font-bold text-white ml-12">
                        {m.title}
                      </h3>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right Column: Text + Counters + CTA */}
          <div className="lg:col-span-6">
            <div className="mb-6">
              <span className="inline-block text-[#ffcc29] font-heading font-bold text-base md:text-lg lg:text-xl tracking-[0.25em] uppercase mb-3">
                OUR STORY
              </span>
              <h2 className="text-white font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight tracking-tight uppercase">
                Crafting Spaces, <br className="hidden sm:inline" />
                <span className="text-[#ffcc29]">Building Legacies</span>
              </h2>
            </div>

            <p className="text-white/90 text-base sm:text-lg font-body leading-relaxed mb-10 font-light">
              From a single interior decoration workshop to an integrated
              turnkey solutions group across Bahrain, UAE, and Saudi Arabia -
              24+ years of craftsmanship, manufacturing and project execution
              under one roof. No salespeople, no marketing budget - our clients
              have been our only ambassadors.
            </p>

            {/* Counters Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
              {counters.map((item, idx) => (
                <div
                  key={idx}
                  className="text-center p-5 rounded-xl bg-white/5 border border-white/10 backdrop-blur-md flex flex-col items-center justify-start"
                >
                  <div className="text-4xl sm:text-5xl lg:text-6xl font-extrabold font-heading text-[#ffcc29] tracking-tight leading-none">
                    {item.value}
                  </div>
                  <div className="text-sm sm:text-base text-white/80 font-body uppercase font-medium tracking-wide mt-2">
                    {item.label}
                  </div>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <div>
              <Link
                to="/about-us"
                className="inline-flex items-center gap-3 bg-[#ffcc29] text-[#002b5c] hover:bg-white transition-all duration-300 font-heading font-bold text-base uppercase tracking-wider px-8 py-4 rounded-lg shadow-lg hover:shadow-xl hover:translate-y-[-2px] group"
              >
                <span>More About Us</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
